import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Trophy, Star, Crown, Flame, UtensilsCrossed,
  Loader2, Medal
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import gustoAvatar from "@/assets/gusto-avatar.png";

// Gusto icon for level display
const GustoIcon = ({ className }) => (
  <img src={gustoAvatar} alt="" className={className} />
);

const LEVEL_ICONS = {
  "utensils": UtensilsCrossed,
  "chef-hat": GustoIcon,
  "flame": Flame,
  "star": Star,
  "crown": Crown,
  "trophy": Trophy,
};

const PODIUM_STYLES = [
  "from-amber-400 to-yellow-500",   // 1st
  "from-gray-300 to-slate-400",     // 2nd
  "from-amber-600 to-orange-700",   // 3rd
];

const LeaderboardPage = () => {
  const { user, token } = useAuth();
  const [leaders, setLeaders] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadersRes, rankRes] = await Promise.all([
          axios.get(`${API}/leaderboard`),
          token
            ? axios.get(`${API}/leaderboard/me`, { headers: { Authorization: `Bearer ${token}` } })
            : Promise.resolve({ data: null }),
        ]);
        setLeaders(leadersRes.data);
        setMyRank(rankRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const top3 = leaders.slice(0, 3);
  const rest = leaders.slice(3);

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="leaderboard-page">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="font-heading text-2xl font-bold text-charcoal">Classement des Chefs</h1>
          <p className="text-sm text-muted-foreground">Les meilleurs cuisiniers de Gusto</p>
        </div>

        {/* My Rank */}
        {myRank && user && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                  #{myRank.rank}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-charcoal">Votre classement</p>
                  <p className="text-xs text-muted-foreground">
                    {myRank.points} pts · {myRank.rank}/{myRank.total} chefs
                  </p>
                </div>
                <Badge className="bg-amber-100 text-amber-700">{myRank.points} pts</Badge>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Podium */}
        {top3.length >= 3 && (
          <div className="flex items-end justify-center gap-3 pt-4">
            {[1, 0, 2].map((idx) => {
              const chef = top3[idx];
              if (!chef) return null;
              const isFirst = idx === 0;
              const Icon = LEVEL_ICONS[chef.level?.icon] || GustoIcon;
              const isMe = user?.id === chef.id;
              return (
                <motion.div
                  key={chef.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.15 }}
                  className={`flex flex-col items-center ${isFirst ? "order-2" : idx === 1 ? "order-1" : "order-3"}`}
                >
                  <div className="relative">
                    {isFirst && (
                      <Crown className="w-6 h-6 text-amber-400 absolute -top-5 left-1/2 -translate-x-1/2" />
                    )}
                    <div className={`w-16 h-16 ${isFirst ? "w-20 h-20" : ""} rounded-full bg-gradient-to-br ${PODIUM_STYLES[chef.rank - 1]} flex items-center justify-center text-white shadow-lg ${isMe ? "ring-3 ring-primary ring-offset-2" : ""}`}>
                      {chef.avatar ? (
                        <img src={chef.avatar} className="w-full h-full rounded-full object-cover" alt="" />
                      ) : (
                        <Icon className={`${isFirst ? "w-8 h-8" : "w-6 h-6"}`} />
                      )}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center text-xs font-bold ${
                      chef.rank === 1 ? "text-amber-500" : chef.rank === 2 ? "text-gray-500" : "text-amber-700"
                    }`}>
                      {chef.rank}
                    </div>
                  </div>
                  <p className={`font-medium text-charcoal mt-2 text-center text-sm ${isFirst ? "text-base" : ""} ${isMe ? "text-primary" : ""}`}>
                    {chef.name}
                  </p>
                  <Badge variant="outline" className={`text-[10px] mt-1 ${isFirst ? "bg-amber-50 text-amber-600 border-amber-200" : ""}`}>
                    {chef.points} pts
                  </Badge>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{chef.level?.name}</p>
                  <div className={`w-full rounded-t-xl mt-2 bg-gradient-to-b ${PODIUM_STYLES[chef.rank - 1]} ${
                    isFirst ? "h-24" : chef.rank === 2 ? "h-16" : "h-12"
                  }`} />
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Top 3 fallback if less than 3 */}
        {top3.length > 0 && top3.length < 3 && (
          <div className="space-y-2">
            {top3.map((chef, i) => (
              <RankRow key={chef.id} chef={chef} isMe={user?.id === chef.id} />
            ))}
          </div>
        )}

        {/* Rest of leaderboard */}
        {rest.length > 0 && (
          <Card>
            <CardContent className="p-3 space-y-1">
              {rest.map((chef, i) => (
                <RankRow key={chef.id} chef={chef} isMe={user?.id === chef.id} index={i} />
              ))}
            </CardContent>
          </Card>
        )}

        {leaders.length === 0 && (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 mx-auto text-muted-foreground/20 mb-4" />
            <p className="text-muted-foreground">Pas encore de classement</p>
            <p className="text-sm text-muted-foreground mt-1">Soyez le premier à gagner des points !</p>
          </div>
        )}
      </div>
    </div>
  );
};

const RankRow = ({ chef, isMe, index = 0 }) => {
  const Icon = LEVEL_ICONS[chef.level?.icon] || GustoIcon;
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
        isMe ? "bg-primary/5 ring-1 ring-primary/20" : "hover:bg-gray-50"
      }`}
      data-testid={`rank-${chef.rank}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        isMe ? "bg-primary text-white" : "bg-gray-100 text-charcoal"
      }`}>
        {chef.rank}
      </div>
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        {chef.avatar ? (
          <img src={chef.avatar} className="w-full h-full object-cover" alt="" />
        ) : (
          <Icon className="w-5 h-5 text-gray-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm truncate ${isMe ? "text-primary" : "text-charcoal"}`}>
          {chef.name} {isMe && <span className="text-xs font-normal text-muted-foreground">(vous)</span>}
        </p>
        <p className="text-[10px] text-muted-foreground">{chef.level?.name}</p>
      </div>
      <Badge variant="outline" className="text-xs font-bold">{chef.points} pts</Badge>
    </motion.div>
  );
};

export default LeaderboardPage;
